import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import BaseForm from 'containers/Base/BaseForm';
import { defaultValues, clonePatrolDefInitialValues } from './options';
import FormDataPopulation from 'components/Forms/Fields/FormDataPopulation';
import {
  closeDetails,
  createError,
  getCurrentPage,
  setLoading,
} from 'reducers/ui';
import { errorHandler } from 'utils/errorMessages';
import { virtualPatrolFormFields } from 'utils/patrol';
import {
  patrolPageActions,
  patrolTaskSelectors,
  patrolDataGridActions,
  patrolTasksNS,
} from 'containers/Pages/Tasks/Patrols';

import { actions as patrolTemplateActions } from 'containers/Pages/Tasks/PatrolTemplates/Modules/dataGridTable';
import {
  getPatrolTaskList,
  getPatrolTemplateDetailTransformed,
} from 'containers/Pages/Tasks/PatrolTemplates/Selectors/patrolTemplateSelectors';
import { isEmpty, cloneDeep, get } from 'lodash';
import {
  patrolDetailSection,
  patrolTaskGeneralSection,
  setUserLocation,
} from './formHelpers';
import { v4 } from 'uuid';
import { MEDIA_TYPE_API_ENUMS } from 'utils/constants/media';
import { MEDIA_LOCATION } from 'services/media';
import { convertToUTCTime } from 'utils/time';
import { TASK_EVIDENCE_TYPE } from 'utils/constants/task';
import API from 'services/api';
import { patrolTemplateSelectors } from '../../PatrolTemplates';
import { PATROL_STATUS } from 'utils/constants/patrol';
import { toJSON } from 'utils/helper/baseTasks';
import { popupText } from 'utils/constants/popupText';
import { translateValue } from 'utils/methods/helpers';

export const setStatusAudit = ({
  patrollerInfo,
  currentPointEvidence, // array of evidences completed or incompleted attached with current point
  synchronisedPatrolCheckpointList, // array of all the checkpoints attached to a patrol and info about whether a CP has been patrolled or not.
  checkPointSeqNumber, // current point text
  listOfRequiredEvidence, // an object containing key(templateID) and values(evidences) of all the evidences of a checkpoint
}) => {
  const successful = {
    timestamp: convertToUTCTime({
      date: new Date(),
    }),
    status: PATROL_STATUS.RESOLVED_SUCCESSFUL.id,
    userId: patrollerInfo.general.id,
  };

  const inProgress = {
    timestamp: convertToUTCTime({
      date: new Date(),
    }),
    status: PATROL_STATUS.IN_PROGRESS.id,
    userId: patrollerInfo.general.id,
  };

  const incomplete = {
    timestamp: convertToUTCTime({
      date: new Date(),
    }),
    status: PATROL_STATUS.RESOLVED_INCOMPLETE.id,
    userId: patrollerInfo.general.id,
  };
  // this should never happen
  if (synchronisedPatrolCheckpointList.length === 0) {
    return new Error('Dev failed to send checkpoint list');
    // return [];
  }
  // is this the last point
  const getAllCheckPointsExceptCurrent = synchronisedPatrolCheckpointList.filter(
    f => f.text !== checkPointSeqNumber && !f.isWaypoint,
  );
  if (getAllCheckPointsExceptCurrent.length === 0) {
    // there is one checkpoint only and is last point
    if (Object.keys(listOfRequiredEvidence).length === 0) {
      // no evidence attached
      return successful;
    }
    // has evidence attached. Check if it is completed or not
    const checkIfCurrentPointsEvidenceIsCollected = currentPointEvidence.find(
      f => !f.completed.value,
    );
    if (checkIfCurrentPointsEvidenceIsCollected) {
      return incomplete;
    }
    return successful;
  }

  const allPatrolled = getAllCheckPointsExceptCurrent.every(ev => ev.patrolled);
  if (allPatrolled) {
    // it is last point and everything else is patrolled
    const checkIfAllEvidencesAreCollected = getAllCheckPointsExceptCurrent
      .filter(f => f.evidence)
      .filter(f => f.evidence.every(ev => !ev.completed));

    if (checkIfAllEvidencesAreCollected.length === 0) {
      // no evidence is attached to any point except current
      // check if current point has evidence and are completed
      if (currentPointEvidence.length > 0) {
        if (currentPointEvidence.find(f => !f.completed.value)) {
          // if any is incomplete then it is unsuccessful
          return incomplete;
        }
        return successful;
      } // has no evidence in current point
      return successful;
    }
    // this means there are some checkpoint with evidence attached that are not completed
    return incomplete;
  }
  // it is not the last point and the status should be inProgress
  return inProgress;
};
// cancel function is used for close down detail panel from this component if required
const BasePatrolTasksForm = ({ editMode, closeForm, closeDetail }) => {
  const [resetValues, setResetValues] = useState(false);
  const [showAbortPopup, setShowAbortPopup] = useState(false);
  const [formValues, setFormValues] = useState();
  const [copyValues, setCopyValues] = useState();

  // actions--------------------------------
  const dispatch = useDispatch();
  const dispatchFetchPatrolDefDetails = useCallback(
    id => {
      dispatch(
        patrolTemplateActions.fetchGridRowDetails({
          id,
          patrolEvidenceList: true,
        }),
      );
    },
    [dispatch],
  );
  const dispatchStartVirtualPatrol = useCallback(
    ({ transformPatrolObject, newTaskUUID, statusAudit, taskSuccessful }) => {
      dispatch(
        patrolPageActions.startVirtualPatrol({
          virtualPatrolInfo: transformPatrolObject,
          newTaskUUID,
          statusAudit,
          taskSuccessful,
        }),
      );
    },
    [dispatch],
  );
  const dispatchUploadVirtualPatrol = useCallback(
    ({ transformPatrolObject }) => {
      dispatch(
        patrolPageActions.uploadVirtualPatrol({
          transformPatrolObject,
        }),
      );
    },
    [dispatch],
  );
  const dispatchAbortVirtualPatrol = useCallback(
    ({ id }) => {
      dispatch(
        patrolPageActions.abortPatrolTask({
          id,
        }),
      );
    },
    [dispatch],
  );
  //-------------Actions-----------------------------------
  const evidenceTemplateTaskList = useSelector(state =>
    getPatrolTaskList(state),
  );
  const dispatchPostEvidenceTask = useCallback(
    task => {
      dispatch(
        patrolPageActions.postEvidenceTask({
          task,
        }),
      );
    },
    [dispatch],
  );
  // Selectors ------------------------------
  const patrolDefDetails = useSelector(
    state => getPatrolTemplateDetailTransformed(state),
    shallowEqual,
  );
  const virtualPatrolDetails = useSelector(
    state => patrolTaskSelectors.getPatrolTaskDetailsRaw(state),
    shallowEqual,
  );
  const synchronisedPatrolCheckpointList = useSelector(
    state => patrolTaskSelectors.getPatrolEvidenceCheckpointList(state),
    shallowEqual,
  );

  const firstLocation = useSelector(
    state => patrolTemplateSelectors.getPatrolFirstLocation(state),
    shallowEqual,
  );
  const userId = useSelector(state => state.user.general.id, shallowEqual);
  const viewName = useSelector(state => getCurrentPage(state), shallowEqual);
  const patrollerInfo = useSelector(state => state.user, shallowEqual);
  const detailPanelOpen = useSelector(
    state => state.ui.detailOpen.patrolTasks,
    shallowEqual,
  );
  //--------------------------------
  const patrolOnChange = vals => {
    if (!isEmpty(vals)) {
      setFormValues(vals);
    }
    // check if the reset values are available and merge it in the current form values
    if (!isEmpty(resetValues)) {
      // The second object will overwrite or add to the base object(vals). undefined values are not copied.
      Object.assign(vals, resetValues); // merge the reset values with the current formik state values
      typeof setResetValues === 'function' && setResetValues(); // clear the values
    }
  };
  const patrolWithEvidenceCheckpoints = ({
    patrolDefDetails,
    synchronisedPatrolCheckpointList,
  }) => {
    const { ...newFormInitial } = clonePatrolDefInitialValues({
      patrolToCloneId: patrolDefDetails?.others?.id,
      patrolToCloneDetails: patrolDefDetails,
      defaultValues,
    });
    return {
      ...newFormInitial,
      checkPointDetail: synchronisedPatrolCheckpointList,
    };
  };

  useEffect(() => {
    // if edit mode is false and detail panel was open, then STOP the fetchDetailSaga from running and rerendering this component
    if (!editMode && detailPanelOpen) {
      dispatch({
        type: 'STOP',
        payload: !isEmpty(patrolDefDetails),
      }); // this will cancel/stop fetchdetails from running
    }
  }, [editMode, patrolDefDetails]);

  /* eslint-disable consistent-return */
  useEffect(() => {
    if (!copyValues && !editMode) return undefined;
    const newFormVals = patrolWithEvidenceCheckpoints({
      synchronisedPatrolCheckpointList,
      patrolDefDetails,
    });
    setResetValues(newFormVals);
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    patrolDefDetails?.detailedInfo?.checkPointDetail,
    patrolDefDetails,
    synchronisedPatrolCheckpointList,
    copyValues,
  ]);

  // clear the detail panel and state if it is open while create button is pressed(edit mode = false)
  useEffect(() => {
    if (!editMode && !isEmpty(patrolDefDetails)) {
      dispatch(closeDetails(patrolTasksNS));
    }
  }, [editMode]);

  // It will automatically clear the detail state if create button is pressed if detail panel is open
  useEffect(() => {
    if (!detailPanelOpen) {
      closeDetail();
    }
  }, [detailPanelOpen]);

  const onChangeCallback = ({ values }) => {
    try {
      setCopyValues(true);
      dispatchFetchPatrolDefDetails(values.completeVirtualPatrol.id);
    } catch (e) {
      setCopyValues(false);
      createError(errorHandler(e));
    }
  };
  const transformStartPatrolObject = ({ firstLocation, patrollerInfo }) => {
    const firstLoc = {
      qrCode: get(firstLocation, 'qrCode'),
      userPoint: {
        latitude: get(firstLocation.point, 'latitude'),
        longitude: get(firstLocation.point, 'longitude'),
        isWaypoint: get(firstLocation.point, 'isWaypoint'),
      },
      timestamp: convertToUTCTime({
        date: new Date(),
      }),
    };

    return {
      patroller: {
        general: {
          ...patrollerInfo.general,
        },
        operational: {
          ...patrollerInfo.operational,
          location: setUserLocation({
            patrollerInfo,
            firstLocation,
          }),
          status: 'AVAILABLE',
        },
        details: {
          reliability: get(patrollerInfo.details, 'reliability'),
          preferredLanguages: get(patrollerInfo.details, 'preferredLanguages'),
          preferredLanguageOnly: get(
            patrollerInfo.details,
            'preferredLanguageOnly',
          ),
          languages: get(patrollerInfo.details, 'languages'),
          qualifications: get(patrollerInfo.details, 'qualifications'),
          services: get(patrollerInfo.details, 'services'),
        },
      },
      firstLocation: firstLoc,
    };
  };

  const formatContentForApi = media => {
    switch (media.type) {
      case TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id: {
        return toJSON(media.contents.value);
      }
      case TASK_EVIDENCE_TYPE.OPTION_SINGLE.id: {
        return toJSON(media.contents.value);
      }
      case TASK_EVIDENCE_TYPE.CHECKBOX.id: {
        return null;
      }
      default:
        return media.contents.value;
    }
  };

  const transformTaskEvidenceForApi = async ({
    currentPointEvidence,
    linkedTemplateTaskId,
  }) => {
    const cp_completedEvidences = cloneDeep(currentPointEvidence);
    const checkIfAllEvidenceCollected = cp_completedEvidences.every(
      ev => ev.completed.value,
    );
    if (!isEmpty(cp_completedEvidences) && checkIfAllEvidenceCollected) {
      // send an api call to upload the media
      /* eslint-disable no-await-in-loop */
      for (const media of cp_completedEvidences) {
        // This type of for loop obeys async await. ForEach will not wait for async function to complete
        const mediaId = v4();
        const service = 'PATROL';
        // upload it and reattach it with task
        if (
          (MEDIA_TYPE_API_ENUMS[media.type] ||
            media.type === TASK_EVIDENCE_TYPE.SIGNATURE.id) &&
          media.completed.value
        ) {
          try {
            const data = {
              isSecure: false,
              service,
              base64: media.contents.value,
            };
            dispatch(setLoading(true));
            const mediaTypeForImage =
              media.type === TASK_EVIDENCE_TYPE.SIGNATURE.id
                ? MEDIA_TYPE_API_ENUMS.IMAGE
                : media.type;

            await API.uploadMediaItem({
              userId,
              mediaId,
              type: mediaTypeForImage,
              data: data,
            });
            media.contents = `${MEDIA_LOCATION}/${userId}/${mediaId}/${mediaTypeForImage}`;
            media.completed = true;
            dispatch(setLoading(false));
          } catch (error) {
            dispatch(setLoading(false));
            console.log('error: ', error);
          }
        } else {
          media.contents = media.completed.value
            ? formatContentForApi(media)
            : null;
          media.completed = media.completed.value;
        }
      }
    }
    const task = cloneDeep(evidenceTemplateTaskList[linkedTemplateTaskId]);
    let newTaskUUID = null;
    if (task && checkIfAllEvidenceCollected) {
      // has a task and all the evidences for assigned task are completed
      newTaskUUID = v4();
      Object.assign(task.tasks[0], {
        evidence: cp_completedEvidences,
        id: newTaskUUID,
        isTemplate: false,
        // taskId of the location becomes the template id for thew new created task
        templateId: linkedTemplateTaskId,
      });
      return {
        taskTemplate: task,
        newTaskUUID,
      };
    }
    return {
      taskTemplate: task,
      newTaskUUID,
    };
  };

  const checkIfTaskSuccessful = ({
    newTaskUUID,
    linkedTemplateTaskId,
    currentPointEvidence,
  }) => {
    if (!linkedTemplateTaskId && !newTaskUUID) {
      // if a templateId is not attached that means there is no evidence attached, it is assumed completed
      return true;
    }
    // if it templateId is attached, make sure every evidence is completed
    return currentPointEvidence.every(s => s.completed.value);
  };

  const transformOtherLocations = ({
    locations,
    linkedTemplateTaskId,
    newTaskUUID,
    currentPointEvidence,
    checkPointSeqNumber,
  }) => {
    const taskSuccessful = checkIfTaskSuccessful({
      // this key is required only when there is an attached task to a checkpoint
      linkedTemplateTaskId,
      newTaskUUID,
      currentPointEvidence,
    });

    const transformedLocs = locations.map(loc => {
      if (
        !loc.definedLocation.point.isWaypoint &&
        linkedTemplateTaskId === loc.definedLocation.taskId &&
        loc.definedLocation.checkPointSequence === checkPointSeqNumber
      ) {
        // is not a waypoint and linkedTemplateTaskId is equal to the templateId set in locations for the click checkpoint
        return {
          ...loc,
          definedLocation: {
            ...loc.definedLocation,
            taskId: linkedTemplateTaskId,
          },
          taskId: newTaskUUID,
          timestamp: convertToUTCTime({
            date: new Date(),
          }),
          ...(linkedTemplateTaskId && newTaskUUID && taskSuccessful),
          userPoint: loc.definedLocation.point,
        };
      }
      return loc;
    });
    return transformedLocs;
  };

  const transformUploadVirtualPatrolObject = ({
    virtualPatrolDetails,
    patrollerInfo,
    synchronisedPatrolCheckpointList,
    linkedTemplateTaskId,
    newTaskUUID,
    currentPointEvidence,
    checkPointSeqNumber,
    listOfRequiredEvidence,
  }) => {
    const newStatusAudit = setStatusAudit({
      patrollerInfo,
      currentPointEvidence,
      synchronisedPatrolCheckpointList,
      checkPointSeqNumber,
      listOfRequiredEvidence,
    });
    const payload = {
      ...virtualPatrolDetails,
      locations: transformOtherLocations({
        locations: virtualPatrolDetails.locations,
        linkedTemplateTaskId,
        newTaskUUID,
        currentPointEvidence,
        checkPointSeqNumber,
      }),
      statusAudit: [...virtualPatrolDetails.statusAudit, newStatusAudit],
      currentStatus: newStatusAudit?.status,
    };
    return payload;
  };

  const uploadPatrolTask = async ({
    currentPointEvidence,
    linkedTemplateTaskId,
    checkPointSeqNumber,
    listOfRequiredEvidence,
  }) => {
    let newTaskID = null;
    // transform the evidences for the task api
    // if first point has evidence attached
    if (linkedTemplateTaskId && !isEmpty(currentPointEvidence)) {
      const { taskTemplate, newTaskUUID } = await transformTaskEvidenceForApi({
        currentPointEvidence,
        linkedTemplateTaskId,
      });
      if (newTaskUUID) {
        // upload the evidence only if any of the evidence given in the CP is collected
        dispatchPostEvidenceTask(taskTemplate);
      }

      newTaskID = newTaskUUID;
    }

    if (checkPointSeqNumber === 1) {
      // transform start patrol object
      const transformPatrolObject = transformStartPatrolObject({
        patrollerInfo,
        firstLocation,
      });
      const newStatusAudit = setStatusAudit({
        checkPointSeqNumber,
        listOfRequiredEvidence,
        currentPointEvidence,
        linkedTemplateTaskId,
        patrollerInfo,
        synchronisedPatrolCheckpointList,
      });
      dispatchStartVirtualPatrol({
        transformPatrolObject,
        newTaskUUID: newTaskID,
        statusAudit: newStatusAudit,
        taskSuccessful: checkIfTaskSuccessful({
          newTaskUUID: newTaskID,
          currentPointEvidence,
          linkedTemplateTaskId,
        }),
      });
      return;
    }
    // transform virtual patrol object
    const transformPatrolObject = transformUploadVirtualPatrolObject({
      virtualPatrolDetails,
      patrollerInfo,
      synchronisedPatrolCheckpointList,
      linkedTemplateTaskId,
      newTaskUUID: newTaskID,
      currentPointEvidence,
      checkPointSeqNumber,
      listOfRequiredEvidence,
    });
    dispatchUploadVirtualPatrol({
      transformPatrolObject,
    });
  };

  const pauseThePatrol = () => {
    if (!detailPanelOpen) {
      // if detail panel isnt open then remove the patrol tasks
      dispatch(
        patrolDataGridActions.setGridRowDetails({
          details: {},
        }),
      );
      dispatch(
        patrolTemplateActions.setGridRowDetails({
          details: {},
        }),
      );
    }
    closeForm();
  };

  const checkIfAbortIsRequired = () => {
    if (virtualPatrolDetails.currentStatus === PATROL_STATUS.IN_PROGRESS.id) {
      setShowAbortPopup(prev => !prev);
      return;
    }
    if (!detailPanelOpen) {
      // if detail panel isnt open then remove the patrol tasks
      dispatch(
        patrolDataGridActions.setGridRowDetails({
          details: {},
        }),
      );
      dispatch(
        patrolTemplateActions.setGridRowDetails({
          details: {},
        }),
      );
    }
    closeForm();
  };

  return (
    <BaseForm
      imageRequired={false}
      allowToArchive={false}
      edit={editMode}
      closeForm={checkIfAbortIsRequired}
      onSubmit={pauseThePatrol}
      initialValues={{}}
      formTitle={
        editMode
          ? `${translateValue('patrolform_edit_title')}-(${
              virtualPatrolDetails?.currentStatus
                ? translateValue(
                    PATROL_STATUS[virtualPatrolDetails.currentStatus]?.name,
                  )
                : translateValue(PATROL_STATUS.NEW.name)
            })`
          : `${translateValue('patrolform_create_title')}-(${
              virtualPatrolDetails?.currentStatus
                ? translateValue(
                    PATROL_STATUS[virtualPatrolDetails.currentStatus]?.name,
                  )
                : translateValue(PATROL_STATUS.NEW.name)
            })`
      }
      onChange={patrolOnChange}
      generalSection={patrolTaskGeneralSection(patrolDefDetails)}
      detailedSection={patrolDetailSection({
        patrolDefDetails,
        surfaceImage: formValues?.siteSurfaceImages,
        uploadPatrolTask: uploadPatrolTask,
      })}
      hoverText={'Start a Virtual Patrol'}
      showPopup={showAbortPopup}
      confirmPopup={{
        popupTitle: popupText.abort.popupTitle,
        popupStatusMessage: popupText.abort.popupStatusMessage,
        confirmBtnHandler: () => {
          // upload the patrol
          dispatchAbortVirtualPatrol({
            id: virtualPatrolDetails.id,
          });
        },
        confirmBtnText: popupText.abort.submitBtnText,
        cancelBtnHandler: () => {
          setShowAbortPopup(prev => !prev);
        },
        cancelBtnText: popupText.abort.cancelBtnText,
        closePopupHandler: () => {
          setShowAbortPopup(prev => !prev);
        },
      }}
      viewName={viewName}
      {...(!editMode && //should enable this button only when the patrol is not started
        !virtualPatrolDetails?.currentStatus && {
          headerContent: (
            <FormDataPopulation
              formHeader="patroldef_dialog_title"
              buttonText="patroldef_button_title"
              submitCallback={onChangeCallback}
              openForm={isEmpty(patrolDefDetails) && !editMode}
              subFields={[virtualPatrolFormFields.template()]}
              initialValues={{}}
            />
          ),
        })}
    />
  );
};

BasePatrolTasksForm.propTypes = {
  editMode: PropTypes.bool,
  closeForm: PropTypes.func.isRequired,
  closeDetail: PropTypes.func,
};

export default BasePatrolTasksForm;
